import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Zap, Sun, Sunrise, Sunset, Moon, Save, Skull, MessageSquare, MonitorPlay, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { serverStore } from '../../stores/server.store';
import { confirmStore } from '../../stores/confirm.store';
import { Card } from '../design/Card';
import { Input } from '../design/Input';
import { cn } from '@/utils/cn';

type QuickAction = { id: string; label: string; icon?: string; command: string; success?: string; confirm?: string };
type QuickGroup = { group: string; items: QuickAction[] };

const ICONS: Record<string, typeof Sun> = { sun: Sun, sunrise: Sunrise, sunset: Sunset, moon: Moon, save: Save, skull: Skull };
// Ruhige, neutrale Knöpfe - Farbe nur im Icon (Design-Regel: Mut an EINER Stelle).
const ICON_TONE: Record<string, string> = {
  sunrise: 'text-orange-300',
  sun: 'text-warn',
  sunset: 'text-ember',
  moon: 'text-[#A5A6F6]',
  save: 'text-online',
  skull: 'text-red-400',
};

const btn = 'flex items-center justify-center gap-1.5 rounded-xl border bg-white/[0.02] text-xs font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * Schnellaktionen fuer Steam-Spiele - pro Spiel im Katalog definiert
 * (steam-catalog.js: quickActions + messageCommands). Ersetzt bei ARK die
 * Minecraft-Knoepfe (Zeit/Wetter/Gamemode/Whitelist), die dort nichts taten.
 * Rendert nichts, wenn das Spiel keine Schnellaktionen definiert.
 */
export const SteamQuickActions = observer(() => {
  const { activeServer } = serverStore;
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState('');

  useEffect(() => { serverStore.loadSteamCatalogOnce(); }, []);

  const appId = Number(String(activeServer?.type || '').split(':')[1]);
  const game = serverStore.steamGamesCache.find((g: any) => g.appId === appId);
  const groups: QuickGroup[] = game?.quickActions || [];
  if (!activeServer || groups.length === 0) return null;

  const run = async (id: string, command: string, success: string) => {
    setBusy(id);
    const res = await serverStore.executeConsoleCommand(activeServer.id, command);
    if (res.success) toast.success(success);
    else toast.error(res.message || 'Befehl fehlgeschlagen');
    setBusy(null);
  };

  const onAction = async (a: QuickAction) => {
    if (a.confirm) {
      const ok = await confirmStore.confirm({ title: a.label, message: a.confirm, confirmText: a.label, variant: 'warning' });
      if (!ok) return;
    }
    run(a.id, a.command, a.success || `${a.label}: ausgeführt`);
  };

  const send = async (kind: 'chat' | 'screen') => {
    const cmd = game?.messageCommands?.[kind];
    const text = msg.trim();
    if (!cmd || !text) return;
    await run(`msg-${kind}`, `${cmd} ${text}`, kind === 'chat' ? 'Nachricht im Chat gesendet' : 'Nachricht auf dem Bildschirm angezeigt');
    setMsg('');
  };

  return (
    <Card className="flex flex-col h-full" noPadding>
      <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-line">
        <div className="flex items-center gap-2 text-sm font-medium text-fg">
          <Zap size={15} className="text-fg-muted" /> Schnellaktionen
        </div>
        <span className="text-xs text-fg-muted truncate">{game.name}</span>
      </div>

      <div className="p-5 space-y-5">
        {groups.map((g) => (
          <div key={g.group}>
            <div className="text-[10px] text-fg-muted font-bold uppercase tracking-wider mb-2">{g.group}</div>
            <div className={cn('grid gap-2', g.items.length >= 4 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-2')}>
              {g.items.map((a) => {
                const Icon = ICONS[a.icon || ''] || Zap;
                return (
                  <button
                    key={a.id}
                    onClick={() => onAction(a)}
                    disabled={!!busy}
                    title={a.command}
                    className={cn(btn, g.items.length >= 4 ? 'flex-col p-3' : 'px-3 py-3', 'text-fg border-line hover:bg-white/5')}
                  >
                    {busy === a.id ? <Loader2 size={18} className="animate-spin" /> : <Icon size={g.items.length >= 4 ? 18 : 16} className={ICON_TONE[a.icon || '']} />}
                    {a.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {game.messageCommands && (
          <div>
            <div className="text-[10px] text-fg-muted font-bold uppercase tracking-wider mb-2">Nachricht an alle Spieler</div>
            <Input
              placeholder="Nachricht eingeben…"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send('chat')}
            />
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button onClick={() => send('chat')} disabled={!msg.trim() || !!busy} className={cn(btn, 'px-3 py-2 text-fg border-line hover:bg-white/5')}>
                {busy === 'msg-chat' ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />} Im Chat
              </button>
              <button onClick={() => send('screen')} disabled={!msg.trim() || !!busy} className={cn(btn, 'px-3 py-2 text-ember border-ember/20 hover:bg-ember/10')}>
                {busy === 'msg-screen' ? <Loader2 size={14} className="animate-spin" /> : <MonitorPlay size={14} />} Auf dem Bildschirm
              </button>
            </div>
          </div>
        )}

        <p className="text-[11px] text-fg-muted">Die Befehle gehen per RCON an den Server. Weitere Admin-Befehle direkt in der Konsole.</p>
      </div>
    </Card>
  );
});
